/**
 * MTE DeviceAuthorize form — Phase 4 auth UI structure (M-C2.0+ pre-infra).
 *
 * Parent insere device code (6-char alphanumeric) que a criança viu num device
 * novo. ZERO fetch / ZERO supabase.
 *
 * Após setup infra: wire ao POST /api/parent/devices/authorize (D-AUTH-04 v1.2).
 * Backend valida code + emite mte_device cookie HttpOnly + redirect.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layout, Header } from '../../components/mte/shell';
import '../../styles/auth.css';
import { apiFetch, ApiError } from '../../lib/auth-fetch';

// Code partagé: 6 chars alphanum uppercase. Format conforme Atlas d1-schema-supabase-v1.2.
export const DeviceCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .length(6, 'Le code doit contenir 6 caractères')
    .regex(/^[A-Z0-9]{6}$/, 'Le code doit être alphanumérique'),
});

export type DeviceCodeForm = z.infer<typeof DeviceCodeSchema>;

interface DeviceAuthorizeProps {
  onSubmit?: (data: DeviceCodeForm) => Promise<void> | void;
}

function DeviceAuthorize(_props: DeviceAuthorizeProps = {}): JSX.Element {
  const navigate = useNavigate();
  const [submitState, setSubmitState] = useState<{ status: 'idle' | 'submitting' | 'success' | 'error'; message: string }>({ status: 'idle', message: '' });

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<DeviceCodeForm>({
    resolver: zodResolver(DeviceCodeSchema),
    defaultValues: { code: '' },
  });

  const onValid = async (_data: DeviceCodeForm): Promise<void> => {
    setSubmitState({ status: 'submitting', message: '' });
    try {
      await apiFetch<{ deviceId: string }>('/api/parent/devices/authorize', {
        method: 'POST',
        body: JSON.stringify({ deviceName: 'Web browser', rememberDays: 30 }),
      });
      setSubmitState({ status: 'success', message: 'Appareil autorisé. Cet appareil est maintenant lié à ton compte pour 30 jours.' });
      setTimeout(() => navigate('/entree/enfant/'), 1500);
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.code === 'parent_only' ? 'Connecte-toi d\'abord en tant que parent.' : e.message)
        : 'Code invalide ou expiré';
      setSubmitState({ status: 'error', message: msg });
    }
  };

  return (
    <Layout context="adulte" width="narrow" header={<Header context="adulte" />}>
      <main className="auth-page">
        <div className="auth-card">
          <h1 className="auth-h1">Autoriser cet appareil</h1>
          <p className="auth-help-text">
            Sur le nouvel appareil de ton enfant, un code à 6 caractères s'affiche. Renseigne-le ci-dessous pour lier cet appareil à ton compte famille.
          </p>
          {submitState.status === 'success' && (
            <div className="auth-message-success" role="status" aria-live="polite" data-testid="device-success-region">
              {submitState.message}
            </div>
          )}
          {submitState.status === 'error' && (
            <div className="auth-error-region" role="alert" aria-live="assertive" data-testid="device-error-region">
              {submitState.message}
            </div>
          )}
          <form className="auth-form" onSubmit={handleSubmit(onValid)} noValidate data-testid="device-form">
            <div className="auth-field">
              <label htmlFor="code" className="auth-label">Code à 6 caractères</label>
              <input
                id="code"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                maxLength={6}
                className="auth-input"
                style={{ letterSpacing: '0.4em', textAlign: 'center', fontFamily: 'monospace', fontSize: 'var(--auth-font-size-xl)' }}
                aria-invalid={Boolean(errors.code)}
                aria-describedby={errors.code ? 'device-code-error' : 'device-code-help'}
                {...register('code')}
              />
              {!errors.code && (
                <span id="device-code-help" className="auth-help-text">
                  Le code est visible sur l'écran de l'enfant.
                </span>
              )}
              {errors.code && <span id="device-code-error" className="auth-error" role="alert">{errors.code.message}</span>}
            </div>
            <button type="submit" className="auth-button-primary" disabled={isSubmitting} data-testid="device-submit">
              {isSubmitting ? 'Vérification…' : 'Autoriser'}
            </button>
          </form>
        </div>
      </main>
    </Layout>
  );
}

export default DeviceAuthorize;
